import { camelCaseObjectKeys } from '/b2b/common/helpers/Format';
import { translate } from '/b2b/common/helpers/i18n';

/**
 * Formats a conversation entity from the API response.
 *
 * @param {object} conversation - Raw conversation from the API
 * @returns {object} Formatted conversation
 */
export const formatConversation = conversation => {
    if (!conversation) return conversation;
    const formatted = camelCaseObjectKeys(conversation);
    return {
        ...formatted,
        // Backend returns conversation_id (camelCased to conversationId) — normalize to id
        id: formatted.id || formatted.conversationId,
        title:
            formatted.title ||
            formatted.subject ||
            formatted.firstMessage ||
            translate('aiAssistant.chat.defaultTitle'),
    };
};

/**
 * Formats a message entity from the API response.
 *
 * @param {object} message - Raw message from the API
 * @returns {object} Formatted message
 */
export const formatMessage = message => {
    if (!message) return message;
    const formatted = camelCaseObjectKeys(message);
    return {
        ...formatted,
        timestamp: formatted.createdAt,
        feedback: formatted.feedback?.sentiment || formatted.metadata?.feedback || null,
        citations: formatted.metadata?.annotations || null,
    };
};

/**
 * Formats a single conversation detail response (with messages).
 *
 * @param {object} response - Raw API response from GET /assistant/chat/:conversationId
 * @returns {object} Formatted conversation with messages
 */
export const formatConversationDetail = (response = {}) => {
    const { messages = [], message_pagination = {}, ...rest } = response;
    const { nextCursor, hasMore, pageSize } = camelCaseObjectKeys(message_pagination);
    const formatted = formatConversation(rest);
    return {
        ...formatted,
        messages: messages.map(formatMessage),
        messagePagination: {
            nextCursor: nextCursor || null,
            hasNext: hasMore || false,
            pageSize: pageSize || null,
        },
    };
};

/**
 * Formats the POST /assistant/chat response (returned by both sendChatMessage
 * and escalateConversation). Camel-cases all keys and normalizes the nested
 * helpscoutConversations array so consumers don't have to know about the
 * snake_case wire shape or run their own camelCase helpers.
 *
 * @param {object} response - Raw API response from POST /assistant/chat
 * @returns {object} Formatted chat response
 */
export const formatChatResponse = (response = {}) => {
    const formatted = camelCaseObjectKeys(response);
    return {
        ...formatted,
        helpscoutConversations: (formatted.helpscoutConversations || []).map(formatConversation),
    };
};

/**
 * Formats the assistant chat conversations response into the standard
 * paginated results format used throughout the app.
 *
 * @param {object} response - Raw API response from GET /assistant/chat
 * @returns {{ results: Array, count: number, hasNextPage: boolean, limit: number, offset: number, page: number, pageSize: number }}
 */
export const formatConversationsResponse = (response = {}) => {
    const {
        conversations = [],
        total_conversations: totalConversations,
        pagination = {},
    } = response;
    const { page, pageSize, totalItems, hasNext } = camelCaseObjectKeys(pagination);
    const results = conversations.map(formatConversation);

    return {
        results,
        count: totalItems || totalConversations || results.length,
        hasNextPage: hasNext || false,
        limit: pageSize || 50,
        offset: ((page || 1) - 1) * (pageSize || 50),
        page: page || 1,
        pageSize: pageSize || 50,
    };
};
